import React, { forwardRef } from 'react'

import {
  errorClasses,
  getErrorInputClasses,
  helperClasses,
  labelClasses,
} from '@/app/components/Form/styles'
import { cn } from '@/app/utils/style'

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, className, id, required, rows = 4, ...props }, ref) => {
    const textareaId = id || `textarea-${label?.toLowerCase().replace(/\s+/g, '-')}`

    // Calculate height based on rows, accounting for focus border
    // Line height: 26px
    // Padding: 16px top + 16px bottom = 32px
    // Focus border: 3px top + 3px bottom = 6px
    const lineHeight = 26
    const padding = 32
    const focusBorder = 6
    const calculatedHeight = Math.ceil(rows * lineHeight + padding + focusBorder)

    return (
      <div className="flex flex-col">
        {label && (
          <label htmlFor={textareaId} className={cn(labelClasses)}>
            {label}
            {required && <span className="text-semantic-error ml-xs">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          style={{ height: `${calculatedHeight}px` }}
          className={cn(
            'input-defaultLight',
            'resize-none',
            getErrorInputClasses(error),
            className,
          )}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
          }
          required={required}
          {...props}
        />

        {error && (
          <span id={`${textareaId}-error`} className={errorClasses}>
            {error}
          </span>
        )}
        {!error && helperText && (
          <span id={`${textareaId}-helper`} className={helperClasses}>
            {helperText}
          </span>
        )}
      </div>
    )
  },
)

Textarea.displayName = 'Textarea'
